import { useState } from 'react'
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react'

interface ActiveFilter {
  id: string
  paramName: string
  conditionName: string
  valueDisplay: string
}

interface SearchSummaryBoxProps {
  query: string
  queryBuilt: boolean
  pubmedTotal: number
  maxResults: number
  liteMode: boolean
  filters: ActiveFilter[]
}

const COLLAPSED_QUERY_MAX = 60

function truncate(s: string, n: number): string {
  if (!s) return ''
  return s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s
}

function buildSnippet(p: SearchSummaryBoxProps): string {
  const lines: string[] = []
  lines.push(`Query: "${p.query}"`)
  lines.push(`Built via Query Builder: ${p.queryBuilt ? 'yes' : 'no'}`)
  lines.push(`PubMed total: ${p.pubmedTotal.toLocaleString()} manuscripts`)
  lines.push(`Search options: max_results=${p.maxResults}, lite_mode=${p.liteMode}`)
  if (p.filters.length === 0) {
    lines.push('Filters: none')
  } else {
    lines.push(`Filters (${p.filters.length}):`)
    for (const f of p.filters) {
      lines.push(`  - ${f.paramName} — ${f.conditionName} — ${f.valueDisplay}`)
    }
  }
  return lines.join('\n')
}

export default function SearchSummaryBox(props: SearchSummaryBoxProps) {
  const { query, queryBuilt, pubmedTotal, maxResults, liteMode, filters } = props
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(buildSnippet(props))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard unavailable (e.g. insecure context); silently ignore.
    }
  }

  const filterSummary = filters.length === 0
    ? 'no filters'
    : `${filters.length} filter${filters.length === 1 ? '' : 's'} applied`

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md mb-4 text-sm">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-gray-100 transition-colors rounded-md"
      >
        <span className="mt-0.5 text-gray-500 flex-shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="font-medium text-gray-700">Query &amp; filters</span>
          {!expanded && (
            <>
              <span className="text-gray-400 mx-1">·</span>
              <span className="font-mono text-gray-700">"{truncate(query, COLLAPSED_QUERY_MAX)}"</span>
              <div className="text-xs text-gray-500 mt-0.5">
                PubMed: {pubmedTotal.toLocaleString()} · {filterSummary}
              </div>
            </>
          )}
        </span>
        <span
          onClick={onCopy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCopy(e as any) }}
          className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
          title="Copy reproducibility snippet"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied!' : 'Copy'}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pl-9 space-y-2 text-gray-700">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Query</div>
            <div className="font-mono text-sm break-all bg-white border border-gray-200 rounded px-2 py-1 mt-1">
              "{query}"
            </div>
            {queryBuilt && (
              <div className="text-xs text-gray-500 mt-1">Built via Query Builder</div>
            )}
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-medium">PubMed total:</span> {pubmedTotal.toLocaleString()} manuscripts
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-medium">Search options:</span> max_results={maxResults}
            {liteMode && <> · lite mode</>}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              Filters {filters.length > 0 && `(${filters.length})`}
            </div>
            {filters.length === 0 ? (
              <div className="text-xs text-gray-500 italic">No filters applied</div>
            ) : (
              <ul className="space-y-0.5">
                {filters.map(f => (
                  <li key={f.id} className="text-xs">
                    <span className="text-gray-500">•</span>{' '}
                    <span className="font-medium text-gray-700">{f.paramName}</span>
                    <span className="text-gray-400"> — </span>
                    <span className="text-gray-700">{f.conditionName}</span>
                    <span className="text-gray-400"> — </span>
                    <span className="font-mono text-gray-700">{f.valueDisplay}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
